import React, { useState, useRef, useEffect } from 'react';
import { JOB_TITLE_MAX_LENGTH } from '../jobPostValidation';

import {
  Input,
  JobBoardLocationAutoCompleteList,
  SCREENS,
  useLocationSelection,
  useMediaQuery,
  useT,
} from '@repo/common';
import PostSection from '../PostSection';
import usePlaceholder from '../usePlaceholder';
import RoleList from './RoleList';
import EmploymentTypeList from './EmploymentTypeList';

import { ReactComponent as InfoIcon } from '@repo/common/assets/icons/16px/icon_16px_info_outline.svg';
import { ReactComponent as LocationIcon } from '@repo/common/assets/icons/20px/icon_20px_location.svg';
import { ReactComponent as ArrowBottomIcon } from '@repo/common/assets/icons/20px/icon_20px_arrow-bottom.svg';
import { ReactComponent as ArrowTopIcon } from '@repo/common/assets/icons/20px/icon_20px_arrow-top.svg';

const JobInfo = ({
  textValues,
  setTextValues,
  errors,
  roleOptions,
  employmentTypeOptions,
  companyLogoField,
}) => {
  const t = useT();
  const ph = usePlaceholder();
  const isMobile = useMediaQuery(`(max-width: ${SCREENS.tabletH - 0.02}px)`);
  const [isRoleOpen, setIsRoleOpen] = useState(false);
  const [isEmploymentTypeOpen, setIsEmploymentTypeOpen] = useState(false);
  const [isLocationOpen, setIsLocationOpen] = useState(false);

  const refs = useRef({
    role: null,
    employment: null,
    location: null,
  });

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (refs.current.role && !refs.current.role.contains(event.target)) {
        setIsRoleOpen(false);
      }

      if (
        refs.current.employment &&
        !refs.current.employment.contains(event.target)
      ) {
        setIsEmploymentTypeOpen(false);
      }

      if (
        refs.current.location &&
        !refs.current.location.contains(event.target)
      ) {
        setIsLocationOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  const locationMessage = errors.workLocation || (
    <span className='inline-flex items-center gap-0.5 text-grayscale-gray04'>
      <InfoIcon className='h-4 w-4 shrink-0 text-grayscale-gray03' />
      {t(
        'This location will be used for search and filtering.',
        '이 위치는 검색과 필터링에 사용됩니다.',
      )}
    </span>
  );

  const handleLocationSelect = (location) => {
    setTextValues((prev) => ({
      ...prev,
      workLocation: location.country
        ? `${location.name}, ${location.country}`
        : location.name,
    }));
    setIsLocationOpen(false);
  };

  const locationField = useLocationSelection({
    value: textValues.workLocation,
    onChange: (e) =>
      setTextValues((prev) => ({ ...prev, workLocation: e.target.value })),
    onFocus: () => setIsLocationOpen(true),
    onSelect: handleLocationSelect,
  });

  const locationList = (
    <JobBoardLocationAutoCompleteList
      searchText={locationField.displayValue}
      onSelect={locationField.handleSelect}
    />
  );

  const isInlineList = isMobile && isLocationOpen;

  const handleTextValueChange = (fieldName) => (e) => {
    setTextValues((prev) => ({
      ...prev,
      [fieldName]: e.target.value,
    }));
  };

  return (
    <PostSection
      title={t('Basic Job Info', '기본 정보')}
      paddingClassName='p-0 tabletH:pt-[30px] tabletH:pb-[50px]'
    >
      <Input
        data-validation-field='company'
        label={t('Company*', '회사명*')}
        placeholder={t('e.g. Koready', '예: 코레디')}
        value={textValues.company}
        state={errors.company ? 'failed' : 'default'}
        showStatusIcon={false}
        message={errors.company}
        onChange={handleTextValueChange('company')}
      />

      {companyLogoField}

      <div
        ref={(el) => (refs.current.location = el)}
        className='relative flex flex-col w-full gap-2'
      >
        <Input
          data-validation-field='workLocation'
          label={t(
            'Work Location (for search visibility)*',
            '근무 지역 (검색 노출용)*',
          )}
          placeholder={ph(
            t(
              'Search city, area, or station (e.g. Seoul, Gangnam)',
              '도시, 지역 또는 역을 검색하세요 (예: 서울, 강남)',
            ),
          )}
          value={locationField.displayValue}
          state={errors.workLocation ? 'failed' : 'default'}
          showStatusIcon={true}
          message={isInlineList ? undefined : locationMessage}
          onChange={locationField.handleChange}
          onFocus={locationField.handleFocus}
          onBlur={locationField.handleBlur}
          leftIcon={<LocationIcon />}
          rightIcon={
            isLocationOpen ? (
              <ArrowTopIcon className='text-icon-placeholder' />
            ) : (
              <ArrowBottomIcon className='text-icon-placeholder' />
            )
          }
          onClick={() => setIsLocationOpen((prev) => !prev)}
        />

        {isInlineList ? (
          <>
            {locationList}
            <div className='typo-b14Strong'>{locationMessage}</div>
          </>
        ) : (
          isLocationOpen && (
            <div className='absolute left-0 z-20 mt-1 w-full top-full'>
              {locationList}
            </div>
          )
        )}
      </div>

      <Input
        data-validation-field='companyAddress'
        label={t('Company address (Detail)*', '회사 주소 (상세)*')}
        placeholder={ph(
          t(
            'Enter full street address (e.g. 123 Teheran-ro, 5F, Gangnam-gu, Seoul)',
            '전체 주소를 입력하세요 (예: 서울 강남구 테헤란로 123, 5층)',
          ),
        )}
        value={textValues.companyAddress}
        state={errors.companyAddress ? 'failed' : 'default'}
        showStatusIcon={false}
        message={errors.companyAddress}
        onChange={handleTextValueChange('companyAddress')}
      />

      <Input
        data-validation-field='jobTitle'
        label={t('Job title*', '공고 제목*')}
        placeholder={ph(
          t(
            'Position + Career (e.g. Backend software engineer, internship)',
            '직무 + 경력 (예: 백엔드 개발자, 인턴)',
          ),
        )}
        value={textValues.jobTitle}
        maxLength={JOB_TITLE_MAX_LENGTH}
        state={errors.jobTitle ? 'failed' : 'default'}
        showStatusIcon={false}
        message={errors.jobTitle}
        onChange={handleTextValueChange('jobTitle')}
      />

      <div ref={(el) => (refs.current.role = el)} className='relative'>
        <Input
          data-validation-field='role'
          label={t('Role*', '직무*')}
          placeholder={ph(t('Select role', '직무를 선택하세요'))}
          value={textValues.role === 'Select role' ? '' : textValues.role}
          state={errors.role ? 'failed' : 'default'}
          showStatusIcon={false}
          message={errors.role}
          readOnly
          rightIcon={
            isRoleOpen ? (
              <ArrowTopIcon className='text-icon-placeholder' />
            ) : (
              <ArrowBottomIcon className='text-icon-placeholder' />
            )
          }
          onClick={() => setIsRoleOpen((prev) => !prev)}
        />

        {isRoleOpen && (
          <div className='absolute left-0 z-20 w-full mt-1 top-full'>
            <RoleList
              options={roleOptions}
              selectedOption={textValues.role}
              onSelect={(option) => {
                setTextValues((prev) => ({
                  ...prev,
                  role: option,
                }));
                setIsRoleOpen(false);
              }}
            />
          </div>
        )}

        {(textValues.role === 'Other (Enter manually)' ||
          textValues.role === 'Other') && (
          <Input
            data-validation-field='customRole'
            className='mt-2'
            label=''
            placeholder={ph(
              t(
                'Enter role title (e.g. Legal Counsel)',
                '직무명을 입력하세요 (예: 사내 변호사)',
              ),
            )}
            value={textValues.customRole}
            state={errors.customRole ? 'failed' : 'default'}
            showStatusIcon={false}
            message={errors.customRole}
            onChange={(e) => {
              const value = e.target.value;
              setTextValues((prev) => ({
                ...prev,
                customRole: value,
              }));
            }}
          />
        )}
      </div>

      <div ref={(el) => (refs.current.employment = el)} className='relative'>
        <Input
          data-validation-field='employmentType'
          label={t('Employment type*', '고용 형태*')}
          placeholder={ph(
            t('Select employment type', '고용 형태를 선택하세요'),
          )}
          value={
            textValues.employmentType === 'Select employment type'
              ? ''
              : textValues.employmentType
          }
          state={errors.employmentType ? 'failed' : 'default'}
          showStatusIcon={false}
          message={errors.employmentType}
          readOnly
          rightIcon={
            isEmploymentTypeOpen ? (
              <ArrowTopIcon className='text-icon-placeholder' />
            ) : (
              <ArrowBottomIcon className='text-icon-placeholder' />
            )
          }
          onClick={() => setIsEmploymentTypeOpen((prev) => !prev)}
        />

        {isEmploymentTypeOpen && (
          <div className='absolute left-0 z-20 w-full mt-1 top-full'>
            <EmploymentTypeList
              options={employmentTypeOptions}
              selectedOption={textValues.employmentType}
              onSelect={(option) => {
                setTextValues((prev) => ({
                  ...prev,
                  employmentType: option,
                }));
                setIsEmploymentTypeOpen(false);
              }}
            />
          </div>
        )}
      </div>
    </PostSection>
  );
};

export default JobInfo;
